import { create } from "zustand";
import { getSession } from "../services/authApi";

// How long to wait on the initial session check before falling back to the login redirect.
export const SESSION_GRACE_MS = 3000;

const EMPTY_SESSION = {
	authenticated: false,
	profile: null,
	userId: null,
	role: null,
	organizationId: null,
	organizationName: "",
	permittedApps: [],
};

function toSessionState(payload) {
	if (!payload?.authenticated) {
		return EMPTY_SESSION;
	}

	return {
		authenticated: true,
		profile: payload.profile || null,
		userId: payload.profile?.id ?? null,
		role: payload.role ?? null,
		organizationId: payload.organization_id ?? null,
		organizationName: payload.organization_name || "",
		permittedApps: Array.isArray(payload.permitted_apps) ? payload.permitted_apps : [],
	};
}

export function getDisplayName(profile) {
	return [profile?.first_name, profile?.last_name].filter(Boolean).join(" ");
}

const useAuthStore = create((set) => ({
	...EMPTY_SESSION,
	initialized: false,
	loading: false,
	error: "",

	clearAuth: () => set({ ...EMPTY_SESSION, error: "" }),

	bootstrapSession: async () => {
		set({ loading: true, error: "" });
		const graceTimer = setTimeout(() => set({ initialized: true }), SESSION_GRACE_MS);
		try {
			const payload = await getSession();
			set({ ...toSessionState(payload), initialized: true, loading: false, error: "" });
		} catch (err) {
			set({
				...EMPTY_SESSION,
				initialized: true,
				loading: false,
				error: err.message || "Failed to load session",
			});
		} finally {
			clearTimeout(graceTimer);
		}
	},

	revalidateSession: async () => {
		try {
			const next = toSessionState(await getSession());
			set({ ...next, error: "" });
			return next.authenticated;
		} catch {
			set({ ...EMPTY_SESSION, error: "" });
			return false;
		}
	},
}));

export default useAuthStore;